// src/modules/app-views/toolbox/pages/Document/tabs/ReferencesTab/components/ReferencesCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Card, Table, Button, Flex, Space, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { toolboxAddToSet } from '../../../../../service';
import ReferenceModal from '../modals/ReferenceModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ReferencesCard = ({ data, disabled, onRefresh }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const dataSource = data?.references || [];

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleAdd = useCallback(() => {
    setEditingRecord(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback(record => {
    setEditingRecord(record);
    setModalOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setModalOpen(false);
    setEditingRecord(null);
  }, []);

  const handleSave = useCallback(
    async formData => {
      setLoading(true);
      try {
        const payload = editingRecord
          ? { _id: editingRecord._id, ...formData }
          : formData;

        await toolboxAddToSet({
          _id: data._id,
          operator: '$push',
          path: 'references',
          data: payload,
        });

        appService.message({ flag: 's', text: 'saved_successfully' });
        setModalOpen(false);
        setEditingRecord(null);
        onRefresh?.();
      } catch (error) {
        console.error('[ReferencesCard] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [data?._id, editingRecord, onRefresh]
  );

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        await toolboxAddToSet({
          _id: data._id,
          operator: '$pull',
          path: 'references._id',
          data: { _id: record._id },
        });

        appService.message({ flag: 's', text: 'removed_successfully' });
        onRefresh?.();
      } catch (error) {
        console.error('[ReferencesCard] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data?._id, onRefresh]
  );

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const columns = buildColumns({ disabled, loading, handleEdit, handleDelete });

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <>
      <Card title={<div className="text-muted">{translateX('technical_references')}</div>}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
        />

        {!disabled && (
          <Flex justify="flex-end" className="mt-3">
            <Button
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={loading}
            >
              {translateX('add_reference')}
            </Button>
          </Flex>
        )}
      </Card>

      <ReferenceModal
        open={modalOpen}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ReferencesCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildColumns({ disabled, loading, handleEdit, handleDelete }) {
  const cols = [
    {
      title: translateX('type'),
      dataIndex: 'type',
      width: 100,
      render: type => {
        if (type === 'video') {
          return <Tag color="red">Video</Tag>;
        }
        if (type === 'website') {
          return <Tag color="blue">Website</Tag>;
        }
        return <span className="text-muted">-</span>;
      },
    },
    {
      title: translateX('title'),
      dataIndex: 'title',
      ellipsis: true,
      render: (text, record) => {
        if (record.url) {
          return (
            <a href={record.url} target="_blank" rel="noopener noreferrer">
              <strong>{text}</strong>
            </a>
          );
        }
        return <strong>{text}</strong>;
      },
    },
    {
      title: translateX('details'),
      key: 'details',
      ellipsis: true,
      render: (_, record) => {
        const detail = record.channel || record.description;
        return detail ? (
          <span className="text-muted">{detail}</span>
        ) : (
          <span className="text-muted">-</span>
        );
      },
    },
  ];

  // actions column only when not disabled
  if (!disabled) {
    cols.push({
      title: translateX('actions'),
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={loading}
            />
          </Popconfirm>
        </Space>
      ),
    });
  }

  return cols;
}
